import { Box, Text } from '@chakra-ui/react';
import type { CSSProperties } from 'react';

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  label?: string;
  /** When true, option values are numbers (coerced back on change). */
  numeric?: boolean;
  ariaLabel?: string;
}

const SELECT_STYLE: CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  fontSize: '13px',
  cursor: 'pointer',
  borderRadius: '6px',
  border: '1px solid var(--chakra-colors-figma-border, rgba(255,255,255,0.08))',
  background: 'var(--chakra-colors-figma-panel, #383838)',
  color: 'var(--chakra-colors-figma-text, #ffffff)',
};

/**
 * Figma-styled select. Native `<select>` under the hood (so it inherits the OS
 * dropdown) but themed to match the kit.
 */
export function Select<T extends string | number>({
  value,
  onChange,
  options,
  label,
  numeric = false,
  ariaLabel,
}: SelectProps<T>) {
  return (
    <Box>
      {label && (
        <Text mb="1.5" fontSize="xs" fontWeight="500" color="figma.textSecondary">
          {label}
        </Text>
      )}
      <select
        aria-label={ariaLabel ?? label}
        value={String(value)}
        onChange={(e) =>
          onChange((numeric ? Number(e.currentTarget.value) : e.currentTarget.value) as T)
        }
        style={SELECT_STYLE}
      >
        {options.map((option) => (
          <option key={String(option.value)} value={String(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    </Box>
  );
}
